// src/pages/home/DogLookup.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./DogLookup.css";
import config from "../../config.json";

const DELETE_PASSWORD = config.password;

// questionnaire labels
const START_LABEL = "Start time:";
const DURATION_LABEL = "Duration: (in minutes)";
const WALK_LOCATION_LABEL = "Location of walk:";
const CAT_LOCATION_LABEL = "Location:";

// ✅ Force all date computations to use America/New_York
const TIME_ZONE = "America/New_York";

// --------- timezone helpers ---------
function nyMDY(dateObj) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dateObj);

  const mm = parts.find((p) => p.type === "month")?.value;
  const dd = parts.find((p) => p.type === "day")?.value;
  const yy = parts.find((p) => p.type === "year")?.value;
  if (!mm || !dd || !yy) return null;
  return `${mm}/${dd}/${yy}`;
}

function nyDateLabelFromMDY(mdy) {
  if (!mdy) return "";
  const [mm, dd, yy] = mdy.split("/").map(Number);
  if (!mm || !dd || !yy) return "";
  // midday avoids DST edge cases
  const d = new Date(yy, mm - 1, dd, 12, 0, 0);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

function ymdToNYMDY(ymd) {
  if (!ymd) return null;
  // midday prevents rolling into previous day
  const d = new Date(`${ymd}T12:00:00`);
  return nyMDY(d);
}

function DogLookup({ location, theme, refreshKey = 0 }) {
  const isCatMode = theme === "cat";

  const [allAnimals, setAllAnimals] = useState([]);
  const [selectedAnimal, setSelectedAnimal] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD
  const [showDropdown, setShowDropdown] = useState(false);

  const wrapperRef = useRef(null);

  // ✅ abort + stale-run protection
  const abortRef = useRef(null);
  const runIdRef = useRef(0);

  // ✅ keep latest selected id so fetch can refresh it safely
  const selectedIdRef = useRef(null);
  useEffect(() => {
    selectedIdRef.current = selectedAnimal?.id || null;
  }, [selectedAnimal]);

  // ✅ cache keys
  const endpoint = isCatMode ? "/api/cats" : "/api/dogs";
  const animalsCacheKey = `animalList:v1:${isCatMode ? "cat" : "dog"}`;
  const selectedCacheKey = `animalSelected:v1:${isCatMode ? "cat" : "dog"}:${location}`;

  // ✅ Pretty popup (same design as your other popups)
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openPopup = (title, message) => {
    setPopup({ open: true, title: title || "", message: message || "" });
  };

  const closePopup = () => {
    setPopup((p) => ({ ...p, open: false }));
  };

  // ---------------------------
  // Helpers: cache-aware setter
  // ---------------------------
  function setAllAnimalsAndCache(next) {
    setAllAnimals(next);
    try {
      localStorage.setItem(animalsCacheKey, JSON.stringify(next));
    } catch {}
  }

  function setSelectedAndCache(next) {
    setSelectedAnimal(next);
    try {
      if (!next) localStorage.removeItem(selectedCacheKey);
      else localStorage.setItem(selectedCacheKey, JSON.stringify(next));
    } catch {}
  }

  // ---------------------------
  // Fetch animals (background) - abort + ignore stale responses
  // ---------------------------
  const fetchAnimals = async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const myRunId = ++runIdRef.current;

    try {
      const response = await fetch(endpoint, { signal: ctrl.signal });
      const data = await response.json();

      if (ctrl.signal.aborted) return;
      if (myRunId !== runIdRef.current) return;

      const nextList = Array.isArray(data) ? data : [];
      setAllAnimalsAndCache(nextList);

      // ✅ if something is selected, refresh it from the newest list
      const selId = selectedIdRef.current;
      if (selId) {
        const fresh = nextList.find((a) => a.id === selId);
        if (fresh) setSelectedAndCache(fresh);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Error fetching animal data:", err);
      setAllAnimalsAndCache([]);
    }
  };

  // ---------------------------
  // Load cache immediately + fetch
  // ---------------------------
  useEffect(() => {
    setAllAnimals([]);
    setSelectedAndCache(null);
    setSearchTerm("");
    setDebouncedSearch("");
    setDateFilter("");
    setShowDropdown(false);

    try {
      const cached = localStorage.getItem(animalsCacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) setAllAnimals(parsed);
      }
    } catch {}

    try {
      const cachedSel = localStorage.getItem(selectedCacheKey);
      if (cachedSel) {
        const parsedSel = JSON.parse(cachedSel);
        if (parsedSel && parsedSel.location === location) {
          setSelectedAnimal(parsedSel);
          setSearchTerm(parsedSel.name || "");
        }
      }
    } catch {}

    fetchAnimals();

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCatMode, location]);

  useEffect(() => {
    fetchAnimals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, isCatMode, location]);

  // ---------------------------
  // Debounce search
  // ---------------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 120);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // click-outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const term = (debouncedSearch || "").toLowerCase();
    const loc = location;

    return (allAnimals || []).filter((a) => {
      const nameOk = (a.name || "").toLowerCase().includes(term);
      const locOk = a.location === loc;
      return nameOk && locOk;
    });
  }, [allAnimals, debouncedSearch, location]);

  function addMinutesToClockString(timeStr, minutesToAdd) {
    if (!timeStr) return null;

    const trimmed = String(timeStr).trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?(\s*(AM|PM))?$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const mins = parseInt(match[2], 10);
    const ampm = match[4] ? match[4].toUpperCase() : null;

    if (ampm) {
      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
    }

    const d = new Date(1970, 0, 1, hours, mins);
    d.setMinutes(d.getMinutes() + Number(minutesToAdd || 0));

    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }

  function clockStringToMinutes(timeStr) {
    if (!timeStr) return null;

    const trimmed = String(timeStr).trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?(\s*(AM|PM))?$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const mins = parseInt(match[2], 10);
    const ampm = match[4] ? match[4].toUpperCase() : null;

    if (ampm) {
      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
    }

    if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
    return hours * 60 + mins;
  }

  function formatFullDateTime(timestamp) {
    const d = new Date(timestamp);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: TIME_ZONE,
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  }

  function formatAnswer(answer) {
    if (Array.isArray(answer)) return answer.join(", ");
    if (answer == null || answer === "") return "N/A";
    return String(answer);
  }

  function getEntryDateKey(entry) {
    if (entry?.id) {
      const m = String(entry.id).match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      if (m) return m[1];
    }
    if (entry?.time) {
      const key = nyMDY(new Date(entry.time));
      if (key) return key;
    }
    return null;
  }

  function dateKeyToStamp(key) {
    if (!key) return 0;
    const [month, day, year] = key.split("/").map(Number);
    const d = new Date(year, month - 1, day, 12, 0, 0);
    return d.getTime();
  }

  function getEntryDateLabel(entry) {
    return nyDateLabelFromMDY(getEntryDateKey(entry));
  }

  function entryMatchesLocation(entry, currentLocation) {
    const qs = Array.isArray(entry?.questionnaire) ? entry.questionnaire : [];
    const locWalk = qs.find((q) => q.question === WALK_LOCATION_LABEL)?.answer;
    const locCat = qs.find((q) => q.question === CAT_LOCATION_LABEL)?.answer;
    if (locWalk && locWalk !== currentLocation) return false;
    if (locCat && locCat !== currentLocation) return false;
    return true;
  }

  const entries = useMemo(() => {
    if (!selectedAnimal) return [];
    if (isCatMode) {
      if (Array.isArray(selectedAnimal.notes)) return selectedAnimal.notes;
      if (Array.isArray(selectedAnimal.walks)) return selectedAnimal.walks;
      return [];
    }
    if (Array.isArray(selectedAnimal.walks)) return selectedAnimal.walks;
    if (Array.isArray(selectedAnimal.notes)) return selectedAnimal.notes;
    return [];
  }, [selectedAnimal, isCatMode]);

  // ✅ totals restricted to current location (all dates)
  const entriesInLocation = useMemo(() => {
    return entries.filter((e) => entryMatchesLocation(e, location));
  }, [entries, location]);

  const filterKeyNY = useMemo(() => {
    if (!dateFilter) return null;
    return ymdToNYMDY(dateFilter);
  }, [dateFilter]);

  const filteredEntries = useMemo(() => {
    const filtered = entries.filter((entry) => {
      if (!entryMatchesLocation(entry, location)) return false;
      if (!filterKeyNY) return true;
      return getEntryDateKey(entry) === filterKeyNY;
    });

    filtered.sort((a, b) => {
      const aStamp = dateKeyToStamp(getEntryDateKey(a));
      const bStamp = dateKeyToStamp(getEntryDateKey(b));
      if (aStamp !== bStamp) return bStamp - aStamp;

      const startQa = a.questionnaire?.find((q) => q.question === START_LABEL);
      const startQb = b.questionnaire?.find((q) => q.question === START_LABEL);

      const aMin = startQa?.answer ? clockStringToMinutes(startQa.answer) : null;
      const bMin = startQb?.answer ? clockStringToMinutes(startQb.answer) : null;

      const aSort = Number.isFinite(aMin) ? aMin : Number.NEGATIVE_INFINITY;
      const bSort = Number.isFinite(bMin) ? bMin : Number.NEGATIVE_INFINITY;

      if (aSort !== bSort) return bSort - aSort;
      return new Date(b.time) - new Date(a.time);
    });

    return filtered;
  }, [entries, location, filterKeyNY]);

  // ✅ IMPORTANT: Totals should be for the selected day IF filtering, otherwise all dates
  const totalsSource = useMemo(() => {
    return filterKeyNY ? filteredEntries : entriesInLocation;
  }, [filterKeyNY, filteredEntries, entriesInLocation]);

  const totalsLabel = useMemo(() => {
    if (!filterKeyNY) return "Totals (all dates)";
    return `Totals for ${nyDateLabelFromMDY(filterKeyNY)}`;
  }, [filterKeyNY]);

  // ✅ Breakdown counts based on totalsSource (so it becomes “that day only” when filtered)
  const activityTotals = useMemo(() => {
    const totals = {
      Walk: 0,
      "Indoor Activity": 0,
      "Outdoor Activity": 0,
      Other: 0,
      Total: 0,
    };

    for (const e of totalsSource) {
      totals.Total += 1;

      const t = (e?.type || "").trim();
      if (t === "Walk") totals.Walk += 1;
      else if (t === "Indoor Activity") totals["Indoor Activity"] += 1;
      else if (t === "Outdoor Activity") totals["Outdoor Activity"] += 1;
      else totals.Other += 1;
    }

    return totals;
  }, [totalsSource]);

  async function handleDelete(entry) {
    const pwd = prompt("Enter delete password:");
    if (!pwd) return;

    // ✅ wrong password -> pretty popup
    if (pwd !== DELETE_PASSWORD) {
      openPopup("Incorrect password", "The password is incorrect.\nPlease try again.");
      return;
    }

    const sameEntry = (x) => x?.id === entry?.id && x?.time === entry?.time;

    try {
      const payload = isCatMode
        ? { catId: selectedAnimal.id, noteId: entry.id, time: entry.time }
        : { dogId: selectedAnimal.id, walkId: entry.id, time: entry.time };

      const res = await fetch("/api/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        openPopup("Delete failed", "Something went wrong.\nPlease try again.");
        return;
      }

      setSelectedAndCache((prev) => {
        if (!prev) return prev;
        const next = { ...prev };

        if (isCatMode) {
          if (Array.isArray(prev.notes)) next.notes = prev.notes.filter((x) => !sameEntry(x));
          else if (Array.isArray(prev.walks)) next.walks = prev.walks.filter((x) => !sameEntry(x));
        } else {
          if (Array.isArray(prev.walks)) next.walks = prev.walks.filter((x) => !sameEntry(x));
          else if (Array.isArray(prev.notes)) next.notes = prev.notes.filter((x) => !sameEntry(x));
        }

        return next;
      });

      setAllAnimalsAndCache(
        (allAnimals || []).map((a) => {
          if (a.id !== selectedAnimal.id) return a;
          const next = { ...a };

          if (isCatMode) {
            if (Array.isArray(a.notes)) next.notes = a.notes.filter((x) => !sameEntry(x));
            else if (Array.isArray(a.walks)) next.walks = a.walks.filter((x) => !sameEntry(x));
          } else {
            if (Array.isArray(a.walks)) next.walks = a.walks.filter((x) => !sameEntry(x));
            else if (Array.isArray(a.notes)) next.notes = a.notes.filter((x) => !sameEntry(x));
          }

          return next;
        })
      );
    } catch (err) {
      console.error("Error deleting entry:", err);
      openPopup("Delete failed", "Something went wrong.\nPlease try again.");
    }
  }

  return (
    <div className="dog-lookup-wrapper">
      <h2>{isCatMode ? "Search Cats" : "Search Dogs"}</h2>

      <div className="dog-lookup-searchbar" ref={wrapperRef}>
        <input
          type="text"
          placeholder={isCatMode ? "Search cat..." : "Search dog..."}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
            setSelectedAndCache(null);
          }}
          onFocus={() => {
            fetchAnimals();
            setShowDropdown(true);
          }}
        />

        {selectedAnimal && (
          <button
            className="clear-button"
            onClick={() => {
              setSelectedAndCache(null);
              setSearchTerm("");
              setDebouncedSearch("");
              setDateFilter("");
              setShowDropdown(false);
            }}
            type="button"
          >
            ×
          </button>
        )}

        {showDropdown && filteredOptions.length > 0 && (
          <ul className="dog-dropdown">
            {filteredOptions.map((a) => (
              <li
                key={a.id}
                onMouseDown={() => {
                  const fresh = allAnimals.find((x) => x.id === a.id);
                  const chosen = fresh || a;

                  setSelectedAndCache(chosen);
                  setSearchTerm(chosen.name || "");
                  setShowDropdown(false);
                }}
              >
                <img
                  src={a.image || `https://robohash.org/${a.name}?set=set4&size=40x40`}
                  alt={a.name}
                  className="dropdown-avatar"
                />
                {a.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedAnimal && (
        <div className="dog-info-card">
          <img
            src={
              selectedAnimal.image ||
              `https://robohash.org/${selectedAnimal.name}?set=set4&size=100x100`
            }
            alt={selectedAnimal.name}
            className="dog-info-avatar"
          />
          <h3>{selectedAnimal.name}</h3>

          <p>
            <strong>Location:</strong> {selectedAnimal.location}
          </p>

          {/* ✅ Totals that clearly become “that day only” when filtering */}
          <div style={{ marginTop: 8, marginBottom: 6 }}>
            <p style={{ marginBottom: 6 }}>
              <strong>{totalsLabel}</strong>
            </p>

            {isCatMode ? (
              <p>
                <strong>Total Notes:</strong> {totalsSource.length}
              </p>
            ) : (
              <>
                <p>
                  <strong>Total Walks:</strong> {activityTotals.Walk}
                </p>
                <p>
                  <strong>Total Indoor Activities:</strong> {activityTotals["Indoor Activity"]}
                </p>
                <p>
                  <strong>Total Outdoor Activities:</strong> {activityTotals["Outdoor Activity"]}
                </p>
                {activityTotals.Other > 0 && (
                  <p>
                    <strong>Other Activities:</strong> {activityTotals.Other}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="dog-info-date-filter">
            <label htmlFor="date-filter">
              <strong>Filter by date:</strong>
            </label>
            <input
              id="date-filter"
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>

          <div className="walk-list">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((entry, i) => {
                const entryDateLabel = getEntryDateLabel(entry);
                const loggedAtFull = formatFullDateTime(entry.time);

                let startTimeDisplay = null;
                let endTimeDisplay = null;

                const startQ = entry.questionnaire?.find((q) => q.question === START_LABEL);
                const durationQ = entry.questionnaire?.find((q) => q.question === DURATION_LABEL);

                if (startQ?.answer) startTimeDisplay = addMinutesToClockString(startQ.answer, 0);

                if (startQ?.answer && durationQ?.answer) {
                  const mins = parseInt(String(durationQ.answer).trim(), 10);
                  if (!Number.isNaN(mins) && mins > 0) {
                    endTimeDisplay = addMinutesToClockString(startQ.answer, mins);
                  }
                }

                const visibleQuestions =
                  Array.isArray(entry.questionnaire)
                    ? entry.questionnaire.filter(
                        (q) =>
                          q.question !== DURATION_LABEL &&
                          q.question !== WALK_LOCATION_LABEL &&
                          q.question !== CAT_LOCATION_LABEL
                      )
                    : [];

                const titleLine = isCatMode
                  ? `${selectedAnimal.name} had note added${
                      startTimeDisplay ? ` at ${startTimeDisplay}` : ""
                    }`
                  : `${selectedAnimal.name} ${
                      entry.type === "Walk"
                        ? "was walked"
                        : entry.type === "Indoor Activity"
                        ? "did an indoor activity"
                        : entry.type === "Outdoor Activity"
                        ? "did an outdoor activity"
                        : "had an activity"
                    }${startTimeDisplay ? ` at ${startTimeDisplay}` : ""}`;

                const endedLabel = isCatMode ? "Ended note at:" : "Ended walk at:";

                return (
                  <details className="walk-entry" key={i}>
                    <summary className="walk-entry-summary">
                      <span className="walk-entry-title">{titleLine}</span>
                      <span className="walk-entry-date">{entryDateLabel}</span>
                    </summary>

                    <div className="walk-entry-details">
                      {visibleQuestions.length > 0 ? (
                        <>
                          {visibleQuestions.map((q, idx) => {
                            let answerDisplay = q.answer;

                            if (q.question === START_LABEL && startTimeDisplay) {
                              answerDisplay = startTimeDisplay;
                            } else if (Array.isArray(q.answer)) {
                              answerDisplay = q.answer.join(", ");
                            } else {
                              answerDisplay = formatAnswer(q.answer);
                            }

                            return (
                              <p key={idx}>
                                <strong>{q.question}</strong> {answerDisplay}
                              </p>
                            );
                          })}

                          {endTimeDisplay && (
                            <p>
                              <strong>{endedLabel}</strong> {endTimeDisplay}
                            </p>
                          )}
                        </>
                      ) : (
                        <p>No questionnaire data.</p>
                      )}

                      <p>
                        <strong>Logged by:</strong> {entry.userFirstName} {entry.userLastName}
                      </p>

                      <p>
                        <strong>Logged at:</strong> {loggedAtFull}
                      </p>

                      <button
                        className="delete-log-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(entry);
                        }}
                        type="button"
                      >
                        Delete Entry
                      </button>
                    </div>
                  </details>
                );
              })
            ) : (
              <p className="walk-list-empty">
                {isCatMode
                  ? `No notes on this date for ${selectedAnimal.name}.`
                  : `No activities on this date for ${selectedAnimal.name}.`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ✅ Pretty popup */}
      {popup.open && (
        <div className="pretty-popup-overlay" onMouseDown={closePopup}>
          <div className="pretty-popup" onMouseDown={(e) => e.stopPropagation()}>
            {popup.title ? <div className="pretty-popup-title">{popup.title}</div> : null}

            <div className="pretty-popup-body">
              {String(popup.message || "")
                .split("\n")
                .map((line, idx) => (
                  <div key={idx} className="pretty-popup-line">
                    {line === "" ? "\u00A0" : line}
                  </div>
                ))}
            </div>

            <button className="pretty-popup-close" onClick={closePopup} type="button">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DogLookup;
